'use client';

import React from 'react';
import Header from '@/components/common/Header';
import Footer from '@/app/homepage/components/Footer';
import { useTranslation } from '@/hooks/useTranslation';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';

export default function AboutUsPage() {
    const { t } = useTranslation();

    const stats = [
        { value: '15K+', label: 'Active Manufacturers', icon: 'BuildingOffice2Icon' },
        { value: '50K+', label: 'Successful Connections', icon: 'LinkIcon' },
        { value: '120+', label: 'Countries Served', icon: 'GlobeAltIcon' },
        { value: '98%', label: 'Satisfaction Rate', icon: 'StarIcon' },
    ];

    const values = [
        { icon: 'ShieldCheckIcon', title: 'Trust & Transparency', desc: 'We maintain the highest standards of integrity' },
        { icon: 'SparklesIcon', title: 'Innovation', desc: 'Continuously improving with cutting-edge technology' },
        { icon: 'UserGroupIcon', title: 'Customer Success', desc: 'Your success is our success' },
    ];

    return (
        <>
            <Header />
            <main className="min-h-screen bg-background">
                {/* Hero */}
                <section className="py-20 px-6 bg-gradient-to-b from-card to-background border-b border-border">
                    <div className="max-w-4xl mx-auto text-center">
                        <h1 className="text-5xl font-headline font-bold text-foreground mb-6">About Us</h1>
                        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                            Connecting global buyers with verified Egyptian textile manufacturers since 2020
                        </p>
                    </div>
                </section>

                {/* Stats */}
                <section className="py-20 px-6">
                    <div className="max-w-6xl mx-auto grid md:grid-cols-4 gap-6">
                        {stats.map((stat, i) => (
                            <div key={i} className="bg-card border border-border rounded-lg p-6 text-center">
                                <Icon name={stat.icon as any} size={32} variant="outline" className="text-primary mx-auto mb-4" />
                                <div className="text-4xl font-bold text-foreground mb-2">{stat.value}</div>
                                <div className="text-sm text-muted-foreground">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Mission */}
                <section className="py-20 px-6 bg-card">
                    <div className="max-w-4xl mx-auto text-center">
                        <h2 className="text-3xl font-headline font-bold text-foreground mb-6">Our Mission</h2>
                        <p className="text-lg text-muted-foreground leading-relaxed">
                            To revolutionize B2B textile sourcing by creating a trusted, transparent marketplace that empowers manufacturers and buyers to build lasting partnerships and drive global commerce.
                        </p>
                    </div>
                </section>

                {/* Values */}
                <section className="py-20 px-6">
                    <div className="max-w-5xl mx-auto">
                        <h2 className="text-3xl font-headline font-bold text-center mb-12">Our Values</h2>
                        <div className="grid md:grid-cols-3 gap-8">
                            {values.map((value, i) => (
                                <div key={i} className="text-center">
                                    <Icon name={value.icon as any} size={48} variant="solid" className="text-primary mx-auto mb-4" />
                                    <h3 className="text-xl font-headline font-semibold text-foreground mb-2">{value.title}</h3>
                                    <p className="text-muted-foreground">{value.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* CTA */}
                <section className="py-20 px-6 bg-card">
                    <div className="max-w-4xl mx-auto text-center">
                        <h2 className="text-3xl font-headline font-bold text-foreground mb-4">Join Our Growing Network</h2>
                        <p className="text-muted-foreground mb-8">Become part of the leading B2B textile sourcing platform</p>
                        <div className="flex flex-wrap justify-center gap-4">
                            <Link href="/manufacturer-directory" className="inline-flex items-center space-x-2 px-8 py-4 bg-primary text-primary-foreground rounded-md font-headline font-semibold hover:bg-primary/90 transition-colors">
                                <span>Browse Manufacturers</span>
                                <Icon name="ArrowRightIcon" size={20} variant="outline" />
                            </Link>
                            <Link href="/contact" className="inline-flex items-center space-x-2 px-8 py-4 bg-background border border-border rounded-md font-headline font-semibold hover:bg-muted transition-colors">
                                <span>Contact Us</span>
                            </Link>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </>
    );
}
